import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { mockData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';

const Hero = () => {
  const { currentLanguage } = useLanguage();
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [youtubeEmbedUrl, setYoutubeEmbedUrl] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [overlayOpacity, setOverlayOpacity] = useState(30);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Récupérer les settings
  useEffect(() => {
    const fetchHeroSettings = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/hero`);
        if (response.ok) {
          const data = await response.json();
          
          // Vidéo MP4 (prioritaire)
          if (data.video_url) {
            // Si c'est une URL relative, ajouter le backend URL
            const url = data.video_url.startsWith('http') 
              ? data.video_url 
              : `${process.env.REACT_APP_BACKEND_URL}${data.video_url}`;
            setVideoUrl(url);
          } else if (data.youtube_video_url) {
            // Fallback YouTube — construire une URL embed autoplay/mute/loop
            let yt = data.youtube_video_url.trim();
            // Normaliser différents formats d'URL vers /embed/<id>
            const match = yt.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?\/#]+)/);
            const videoId = match ? match[1] : null;
            if (videoId) {
              const embed = `https://www.youtube.com/embed/${videoId}`
                + `?autoplay=1&mute=1&loop=1&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3`
                + `&playlist=${videoId}`;
              setYoutubeEmbedUrl(embed);
              setIsVideoLoaded(true);
            }
          }
          
          // Image de couverture
          if (data.cover_image_url) {
            const url = data.cover_image_url.startsWith('http') 
              ? data.cover_image_url 
              : `${process.env.REACT_APP_BACKEND_URL}${data.cover_image_url}`;
            setCoverImage(url);
          }
          
          if (data.overlay_opacity !== undefined) {
            setOverlayOpacity(data.overlay_opacity);
          }
        }
      } catch (error) {
        console.error('Erreur chargement hero settings:', error);
      }
    };
    
    fetchHeroSettings();
  }, []);

  // Gérer la lecture vidéo
  useEffect(() => {
    if (videoRef.current && videoUrl) {
      const video = videoRef.current;
      
      const playVideo = async () => {
        try {
          video.muted = true;
          await video.play();
          setIsVideoLoaded(true);
        } catch (e) {
          console.log('Autoplay bloqué, en attente interaction');
        }
      };

      // Essayer de jouer quand la vidéo est prête
      video.addEventListener('canplay', playVideo);
      
      // Aussi sur interaction utilisateur
      const handleInteraction = () => {
        playVideo();
        document.removeEventListener('touchstart', handleInteraction);
        document.removeEventListener('click', handleInteraction);
      };

      document.addEventListener('touchstart', handleInteraction, { passive: true, once: true });
      document.addEventListener('click', handleInteraction, { passive: true, once: true });

      return () => {
        video.removeEventListener('canplay', playVideo);
        document.removeEventListener('touchstart', handleInteraction);
        document.removeEventListener('click', handleInteraction);
      };
    }
  }, [videoUrl]);

  // Traductions
  const { translatedText: titleTranslated } = useTranslate(mockData.hero.title);
  const { translatedText: subtitleTranslated } = useTranslate(mockData.hero.subtitle);
  const { translatedText: historyBtnTranslated } = useTranslate("L'histoire");
  const { translatedText: publicationsBtnTranslated } = useTranslate("Publications");
  const { translatedText: videoLabelTranslated } = useTranslate("LES LIGNAGES DE BRUXELLES");
  const { translatedText: discoverTranslated } = useTranslate("Découvrez notre histoire");

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Video/Image Background */}
      <div className="absolute inset-0 w-full h-full">
        {/* Image de couverture (toujours présente, cachée quand vidéo chargée) */}
        {coverImage && (
          <div 
            className={`absolute inset-0 w-full h-full bg-cover bg-center transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-0' : 'opacity-100'}`}
            style={{ backgroundImage: `url(${coverImage})` }}
          />
        )}
        
        {/* Vidéo MP4 */}
        {videoUrl && (
          <video
            ref={videoRef}
            className={`absolute w-full h-full object-cover transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}
            autoPlay
            muted
            loop
            playsInline
            webkit-playsinline="true"
            preload="auto"
            poster={coverImage || undefined}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        )}
        
        {/* Vidéo YouTube (fallback si pas de MP4) */}
        {!videoUrl && youtubeEmbedUrl && (
          <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
            <iframe
              src={youtubeEmbedUrl}
              title="Vidéo d'arrière-plan"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] h-[56.25vw] min-w-full min-h-full"
              data-testid="hero-youtube-iframe"
            ></iframe>
          </div>
        )}
        
        {/* Fallback si pas de vidéo ni d'image */}
        {!videoUrl && !youtubeEmbedUrl && !coverImage && (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-800 to-gray-900" />
        )}
        
        {/* Overlays */}
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        <div 
          className="absolute inset-0 bg-black transition-opacity duration-300" 
          style={{ opacity: overlayOpacity / 100 }}
        ></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32">
        <div className="max-w-3xl mx-auto text-center">
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
            style={{ fontFamily: '"Lora", serif' }}
          >
            <span className="text-white drop-shadow-2xl">{titleTranslated}</span>
          </h1>
          
          <p 
            className="text-lg md:text-xl text-white leading-relaxed mb-8 drop-shadow-lg max-w-2xl mx-auto"
            style={{ fontFamily: '"Lora", serif' }}
          >
            {subtitleTranslated}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="hover:bg-opacity-80 text-white px-8 py-3 font-medium shadow-xl"
              style={{ 
                fontFamily: '"Lora", serif',
                backgroundColor: 'rgb(196, 150, 96)'
              }}
              onClick={() => window.location.href = '/lignages'}
            >
              {historyBtnTranslated}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-2 border-white text-white hover:bg-white hover:text-[#2c3e50] px-8 py-3 font-medium shadow-xl backdrop-blur-sm"
              style={{ fontFamily: '"Lora", serif' }}
              onClick={() => window.location.href = '/publications-categories'}
            >
              {publicationsBtnTranslated}
            </Button>
          </div>
        </div>
      </div>

      {/* Indicateur vidéo */}
      <div className="absolute bottom-4 right-4 z-20 bg-black bg-opacity-50 rounded-lg px-3 py-2">
        <p className="text-white text-sm font-medium">
          🎥 {videoLabelTranslated}
        </p>
        <p className="text-white text-xs opacity-75">
          {discoverTranslated}
        </p>
      </div>
    </section>
  );
};

export default Hero;
